/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import moment, { Moment } from 'moment';
import styled from 'styled-components';
import handleFileExport from '@vendys-frontend/excel-export';
import { Tabs, Table, type TableColumnsType, Button } from 'antd';

import { DateRangeNavigation } from 'components';
import { useGetMealPlanSurveyStats } from 'hooks';

import SurveyStatsExcelLayout from './excelLayout/SurveyStatsExcelLayout.json';
import SurveyCommentsExcelLayout from './excelLayout/SurveyCommentsExcelLayout.json';

export const AntdTabs = styled(Tabs)`
  & .ant-tabs-nav-wrap .ant-tabs-nav-list {
    width: 100%;

    .ant-tabs-tab {
      justify-content: center;
      width: 100%;
      margin: 0;
    }
    .ant-tabs-ink-bar.ant-tabs-ink-bar-animated {
      width: 100%;
    }
  }
`;

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 8px 16px;

  .tab-title {
    font-size: 16px;
    font-weight: 700;
  }

  button {
    height: 38px;
    padding: 0px 16px;
    border-radius: 5px;
  }
`;

const SurveyTable = styled(Table)`
  .ant-table-thead th.group-divider {
    position: relative;
  }
  .ant-table-thead th.group-divider::after {
    content: '';
    position: absolute;
    top: 25%;
    bottom: 25%;
    right: 0;
    width: 1px;
    background: #f0f0f0;
  }
`;

const tabItems = [
  { key: 'surveyStats', label: '식단표 만족도' },
  { key: 'comments', label: '식단표 의견' }
];

const surveyColumns: TableColumnsType = [
  { title: '날짜', dataIndex: 'serveDate', width: '100px' },
  { title: '카테고리', dataIndex: 'categoryName', width: '100px' },
  { title: '식단표 명', dataIndex: 'mealPlanName' },
  {
    title: '만족도 평가',
    onHeaderCell: () => ({ className: 'group-divider' }),
    children: [
      { title: '아쉬워요', dataIndex: 'satisfaction1ScoreCount', align: 'center', width: '90px' },
      { title: '그럭저럭', dataIndex: 'satisfaction2ScoreCount', align: 'center', width: '90px' },
      { title: '보통', dataIndex: 'satisfaction3ScoreCount', align: 'center', width: '90px' },
      { title: '좋아요', dataIndex: 'satisfaction4ScoreCount', align: 'center', width: '90px' },
      { title: '최고예요', dataIndex: 'satisfaction5ScoreCount', align: 'center', width: '90px' }
    ]
  },
  {
    title: '식사량 평가',
    onHeaderCell: () => ({ className: 'group-divider' }),
    children: [
      { title: '너무 많음', dataIndex: 'mealQuantity1ScoreCount', align: 'center', width: '90px' },
      { title: '적당함', dataIndex: 'mealQuantity2ScoreCount', align: 'center', width: '90px' },
      { title: '너무 적음', dataIndex: 'mealQuantity3ScoreCount', align: 'center', width: '90px' }
    ]
  },
  { title: '총 설문 수', dataIndex: 'totalSurveyResponseCount', align: 'center', width: '90px' }
];

const commentsColumns: TableColumnsType = [
  { title: '날짜', dataIndex: 'serveDate', width: '120px' },
  { title: '카테고리', dataIndex: 'categoryName', width: '120px' },
  { title: '식단표 명', dataIndex: 'mealPlanName' },
  { title: '의견', dataIndex: 'comment' },
  {
    title: '의견 작성 일시',
    dataIndex: 'createDate',
    width: '200px',
    render: (createDate: string) => (createDate ? moment(createDate).format('YYYY-MM-DD HH:mm:ss') : '')
  }
];

const SurveyStats = () => {
  const [selectedTab, setSelectedTab] = React.useState<string>('surveyStats');
  const [serveDate, setServeDate] = React.useState<Record<string, Moment>>({
    startDate: moment().startOf('month'),
    endDate: moment()
  });

  const startDate = serveDate.startDate.format('YYYY-MM-DD');
  const endDate = serveDate.endDate.format('YYYY-MM-DD');
  const { data } = useGetMealPlanSurveyStats({ startDate, endDate });

  const handleDateChange = (values) => {
    if (values?.startDate && values?.endDate) {
      setServeDate({ startDate: moment(values.startDate), endDate: moment(values.endDate) });
    }
  };

  const handleTabChange = (tabKey: string) => setSelectedTab(tabKey);

  const surveyStatsExcelDownload = () => {
    const fileName = `식단표별 만족도 내역_${moment().valueOf()}.xlsx`;
    const newRows = data?.stats?.map((item) => {
      return [
        item.serveDate,
        item.categoryName,
        item.mealPlanName,
        item.satisfaction1ScoreCount,
        item.satisfaction2ScoreCount,
        item.satisfaction3ScoreCount,
        item.satisfaction4ScoreCount,
        item.satisfaction5ScoreCount,
        item.mealQuantity1ScoreCount,
        item.mealQuantity2ScoreCount,
        item.mealQuantity3ScoreCount,
        item.totalSurveyResponseCount
      ];
    });
    const apiExcelDatas = { 0: { startRowNum: 2, newRows } };
    handleFileExport(SurveyStatsExcelLayout, apiExcelDatas, fileName);
  };

  const commentsExcelDownload = () => {
    const fileName = `식단표 의견_${moment().valueOf()}.xlsx`;
    const newRows = data?.comments?.map((item) => [
      item.serveDate,
      item.categoryName,
      item.mealPlanName,
      item.comment,
      item.createDate ? moment(item.createDate).format('YYYY-MM-DD HH:mm:ss') : ''
    ]);
    const apiExcelDatas = { 0: { startRowNum: 2, newRows } };
    handleFileExport(SurveyCommentsExcelLayout, apiExcelDatas, fileName);
  };

  const handleExcelDownload = () => {
    if (selectedTab === 'surveyStats') {
      surveyStatsExcelDownload();
    } else {
      commentsExcelDownload();
    }
  };

  return (
    <div className="salesstats-content" id="salesstats" style={{ background: '#ffffff' }}>
      <DateRangeNavigation startDate={serveDate.startDate} endDate={serveDate.endDate} onChange={handleDateChange} month={6} />
      <AntdTabs items={tabItems} onChange={handleTabChange} />
      <div>
        <Wrapper>
          <span className="tab-title">{selectedTab === 'surveyStats' ? '식단표별 만족도 내역' : '식단표 의견'}</span>
          <Button type="primary" onClick={handleExcelDownload}>
            엑셀 다운로드
          </Button>
        </Wrapper>
        {selectedTab === 'surveyStats' ? (
          <SurveyTable columns={surveyColumns} dataSource={data?.stats || []} pagination={false} />
        ) : (
          <Table columns={commentsColumns} dataSource={data?.comments || []} pagination={false} />
        )}
      </div>
    </div>
  );
};

export default SurveyStats;
